import { useEffect } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Subtle scale feedback on hover/press — used by the base Button component,
 * app-wide. Hover nudges up (~1.02x) so every button gets a tactile "this is
 * clickable" cue; pressing overrides that down to 0.97x for a brief tap
 * feel; releasing/leaving settles back to 1x. Driven entirely through GSAP
 * (never a CSS `hover:scale-*` class alongside it) — GSAP sets an inline
 * `transform`, which beats a CSS class in the cascade, so mixing the two
 * would leave the hover scale permanently stuck after the first press/
 * release. Skipped entirely under prefers-reduced-motion.
 */
export function useButtonPressAnimation(ref, { hoverScale = 1.02 } = {}) {
  useEffect(() => {
    const node = ref.current;
    if (!node || prefersReducedMotion()) return undefined;

    const onEnter = () => gsap.to(node, { scale: hoverScale, duration: 0.2, ease: 'power2.out' });
    const onDown = () => gsap.to(node, { scale: 0.97, duration: 0.1, ease: 'power1.out' });
    const onUp = () => gsap.to(node, { scale: 1, duration: 0.2, ease: 'power2.out' });

    node.addEventListener('pointerenter', onEnter);
    node.addEventListener('pointerdown', onDown);
    node.addEventListener('pointerup', onUp);
    node.addEventListener('pointerleave', onUp);

    return () => {
      node.removeEventListener('pointerenter', onEnter);
      node.removeEventListener('pointerdown', onDown);
      node.removeEventListener('pointerup', onUp);
      node.removeEventListener('pointerleave', onUp);
    };
  }, [ref, hoverScale]);
}
